import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUIStore } from '@/stores/ui.store'
import { contratoService } from '@/services/contrato.service'
import { formatCurrency, formatDate } from '@/lib/formatters'
import { Search, FileX, ChevronLeft, ChevronRight, Loader2, User, Car, Calendar } from 'lucide-react'

export function CancelamentosPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const navigate = useNavigate()
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [data, setData] = useState<any[]>([])
    const [total, setTotal] = useState(0)
    const [totalPages, setTotalPages] = useState(1)
    const [loading, setLoading] = useState(true)

    useEffect(() => { setPageTitle('Cancelamentos') }, [setPageTitle])

    useEffect(() => {
        setLoading(true)
        contratoService.list({ search, status: 'cancelado', page, pageSize: 15 }).then(r => {
            setData(r.data)
            setTotal(r.total)
            setTotalPages(r.totalPages)
        }).finally(() => setLoading(false))
    }, [search, page])

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h2 className="text-xl font-bold text-surface-900">Cancelamentos</h2>
                <p className="text-sm text-surface-700/60 mt-0.5">{total} contrato(s) cancelado(s)</p>
            </div>

            {/* Filters */}
            <div className="glass-card p-4">
                <div className="relative">
                    <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <input type="text" value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                        placeholder="Buscar por associado ou serviço..."
                        className="input pl-10 w-full" />
                </div>
            </div>

            {/* Table */}
            <div className="glass-card overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 size={24} className="animate-spin text-primary-600" />
                    </div>
                ) : data.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                        <FileX size={40} className="text-surface-700/20" />
                        <p className="text-surface-700/60">Nenhum cancelamento encontrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Associado</th>
                                    <th>Serviço</th>
                                    <th>Veículo</th>
                                    <th>Valor Mensal</th>
                                    <th>Início</th>
                                    <th>Cancelamento</th>
                                    <th>Motivo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((c) => (
                                    <tr key={c.id}
                                        className="hover:bg-surface-50 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/admin/contratos/${c.id}`)}>
                                        {/* Associado */}
                                        <td>
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-xl bg-danger-100 flex items-center justify-center flex-shrink-0">
                                                    <User size={14} className="text-danger-600" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-sm text-surface-900 truncate">{c.associado_nome || '—'}</p>
                                                    <p className="text-xs text-surface-700/40">{c.associado_cpf || ''}</p>
                                                </div>
                                            </div>
                                        </td>
                                        {/* Serviço */}
                                        <td>
                                            <span className="text-sm text-surface-700">
                                                {c.metadata?.servico_nome || c.plano || '—'}
                                            </span>
                                        </td>
                                        {/* Veículo */}
                                        <td>
                                            {c.veiculo_placa ? (
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Car size={13} className="text-surface-700/40" />
                                                    <span className="font-mono text-sm">{c.veiculo_placa}</span>
                                                </span>
                                            ) : (
                                                <span className="text-sm text-surface-700/40">Sem veículo</span>
                                            )}
                                        </td>
                                        {/* Valor */}
                                        <td>
                                            <p className="font-semibold text-surface-900">{formatCurrency(c.valor_total_mensal)}</p>
                                        </td>
                                        {/* Início */}
                                        <td className="text-sm text-surface-700">{formatDate(c.data_inicio)}</td>
                                        {/* Cancelamento */}
                                        <td>
                                            {c.data_cancelamento ? (
                                                <span className="inline-flex items-center gap-1 text-sm text-danger-600">
                                                    <Calendar size={13} />
                                                    {formatDate(c.data_cancelamento)}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        {/* Motivo */}
                                        <td>
                                            <span className="text-xs text-surface-700/60">
                                                {c.metadata?.motivo_cancelamento === 'adesao_nao_paga_5d'
                                                    ? 'Adesão não paga (+5d)'
                                                    : c.metadata?.motivo_cancelamento === 'mensalidade_suspensa_fim_mes'
                                                        ? 'Suspenso fim do mês'
                                                        : c.metadata?.motivo_cancelamento || 'Manual'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-surface-100">
                        <p className="text-sm text-surface-700/60">Página {page} de {totalPages}</p>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30"><ChevronLeft size={16} /></button>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30"><ChevronRight size={16} /></button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
